// navigation-completion Stage E — frontend API client for the current/prior
// remote-hyperlink TARGET inventory and exact-bytes preview.
//
// A SEPARATE module from v2Client.ts (which this phase does not edit); it reuses
// v2Client's exported `apiFetch` so auth headers, base URL and error handling
// are shared, never re-implemented. The wire types mirror the server shapes in
// src/services/navigation/targetService.ts EXACTLY — the UI reads the server
// inventory (opaque target ids, a context version, the authoritative target
// hash) and never invents a path, a hash or a storage key.
//
// Two reads only (both Documents:read):
//   • getInventory — the current/prior candidate destinations for one source
//     document, with an inventory `contextVersion` the caller must echo verbatim
//     when it later resolves a chosen target. A 409 on resolve means the
//     inventory moved and the reviewer must refresh — NEVER an automatic retry.
//   • preview — the EXACT bytes of ONE target, gated server-side by the caller's
//     echoed contextVersion + expectedTargetSha256. Fetched as a Blob so the
//     reviewer can render/download precisely what a chosen destination resolves
//     against; the storage URI is never exposed to the client.

import { apiFetch } from '@/api/v2Client';

export type TargetScope = 'current' | 'prior';
export type LeafFormat = 'pdf' | 'docx' | 'other';

/** One candidate link destination. `id` is OPAQUE and server-authoritative — it
 *  encodes nothing a client can turn into a storage key; it is echoed back (not
 *  dereferenced) on resolution and matched against a freshly-built inventory. */
export interface NavigationTarget {
  readonly id: string;
  readonly label: string;
  readonly scope: TargetScope;
  /** The hash of the EXACT authoritative bytes a destination is verified against;
   *  echoed as `expectedTargetSha256` on preview and resolution. */
  readonly artifactSha256: string;
  readonly format: LeafFormat;
  readonly sequenceFolder: string;
  /** Sequence-folder-relative leaf path (never a storage key). */
  readonly leafPath: string;
  /** The canonical source-relative GoToR href this target is referenced by,
   *  computed authoritatively by the SERVER (one href algorithm, never a client
   *  copy). Matched against a saved remote link's stored path to recover its
   *  authorised target. */
  readonly href: string;
  /** Null in the inventory list: geometry is confirmed per-target at resolution
   *  against exact bytes, not by loading every candidate up front. A null page
   *  count is NOT a known "0 pages"; the UI must not fake one. */
  readonly pageCount: number | null;
  readonly namedDestinations: readonly string[] | null;
  /** True when this leaf can be a page/named GoToR destination NOW. A Word leaf,
   *  or a PDF only produced at compile, is listed but not selectable. */
  readonly available: boolean;
  readonly unavailableReason: string | null;
}

export interface SourcePlacement {
  readonly documentId: string;
  readonly sequenceFolder: string;
  readonly leafPath: string;
  readonly format: LeafFormat;
}

export interface TargetInventory {
  readonly ok: true;
  readonly contextVersion: string;
  readonly sourcePlacement: SourcePlacement;
  readonly targets: readonly NavigationTarget[];
}

/** The destination inside a chosen target. GoToR `/D` is MANDATORY — a remote
 *  jump always names a page or a named destination. `pageIndex` is 0-based on the
 *  wire (the UI collects a 1-based page and converts). */
export type TargetDestination =
  | { readonly kind: 'page'; readonly pageIndex: number }
  | { readonly kind: 'named'; readonly name: string };

export interface SelectedTargetDetails {
  readonly ok: true;
  readonly format: LeafFormat;
  readonly pageCount: number;
  readonly namedDestinations: readonly string[] | null;
  readonly namedDestinationsEnumerable: boolean;
}

export interface TargetPreviewQuery {
  readonly contextVersion: string;
  readonly expectedTargetSha256: string;
}

function base(documentId: string): string {
  return `/api/v1/documents/${encodeURIComponent(documentId)}/navigation/targets`;
}

export const navigationTargets = {
  /** The current/prior candidate destinations for a source document. Read-only;
   *  creates no records. Rejects with ApiError (409 SourceNotConfirmed etc.). */
  getInventory: (documentId: string): Promise<TargetInventory> => apiFetch(base(documentId)),

  details: (documentId: string, targetId: string, q: TargetPreviewQuery): Promise<SelectedTargetDetails> => {
    const params = new URLSearchParams({ contextVersion: q.contextVersion, expectedTargetSha256: q.expectedTargetSha256 });
    return apiFetch(`${base(documentId)}/${encodeURIComponent(targetId)}/details?${params.toString()}`);
  },

  /** The authed EXACT bytes of ONE target (raw response). The caller turns it
   *  into a Blob/object URL and is responsible for revoking that URL. */
  preview: (documentId: string, targetId: string, q: TargetPreviewQuery): Promise<Response> => {
    const params = new URLSearchParams({
      contextVersion: q.contextVersion,
      expectedTargetSha256: q.expectedTargetSha256,
    });
    return apiFetch(`${base(documentId)}/${encodeURIComponent(targetId)}/preview?${params.toString()}`, {
      raw: true,
    });
  },
};
